import { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import QRCode from 'qrcode'

interface QrCodeProps {
  /** Conteúdo codificado no QR. Para o MFA, a `otpauth://` vinda da API. */
  value: string
  /** Lado da imagem, em pixels. */
  size?: number
  /** Texto do `alt`, para quem usa leitor de tela. */
  alt?: string
  /**
   * O que mostrar se a geração falhar. Existe para que a tela nunca fique com
   * um buraco onde deveria haver o QR: no MFA, o segredo em blocos de 4 já é
   * renderizado logo abaixo e continua sendo um caminho válido de vinculação.
   */
  fallback?: React.ReactNode
}

/**
 * Renderiza um QR Code a partir de uma string, no cliente.
 *
 * Burro de propósito: não sabe o que é MFA, não busca nada na API e não guarda
 * nada. Recebe texto, devolve imagem.
 *
 * A geração é assíncrona (`toDataURL` devolve promessa) e roda só no browser —
 * o `useEffect` não executa no SSR do Next, então a primeira pintura no
 * servidor sai com o spinner e a imagem aparece na hidratação. É o
 * comportamento desejado: a URI do `otpauth://` carrega o segredo TOTP e não
 * tem por que passar pelo HTML renderizado no servidor.
 */
export function QrCode({
  value,
  size = 220,
  alt = 'QR Code',
  fallback = null
}: QrCodeProps): JSX.Element {
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    // `cancelled` evita setState depois do unmount e, principalmente, evita que
    // a promessa de um `value` antigo sobrescreva a imagem do `value` novo.
    let cancelled = false

    setDataUrl(null)
    setFailed(false)

    if (!value) {
      setFailed(true)
      return undefined
    }

    QRCode.toDataURL(value, { width: size, margin: 1 })
      .then(url => {
        if (!cancelled) {
          setDataUrl(url)
        }
      })
      .catch(() => {
        // Sem toast: a alternativa (o segredo manual) já está na tela, e um
        // erro vermelho aqui faria o usuário achar que o login quebrou.
        if (!cancelled) {
          setFailed(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [value, size])

  if (failed) {
    return <>{fallback}</>
  }

  if (!dataUrl) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ width: size, height: size }}
      >
        <Spinner animation="border" variant="primary" role="status">
          <span className="sr-only">Gerando QR Code...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <img
      src={dataUrl}
      alt={alt}
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  )
}
